import {
  Button as RACButton,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Popover,
} from "react-aria-components";
import { type CSSProperties } from "react";
import { type CalendarDateTime, parseDateTime } from "@internationalized/date";

import "./datetime.css";

interface DateTimeFieldProps {
  value: string;
  onChange: (next: string) => void;
  id?: string;
  ariaLabel?: string;
  required?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  style?: CSSProperties;
}

function safeParse(value: string): CalendarDateTime | null {
  if (!value) return null;
  try {
    // datetime-local can come as "YYYY-MM-DDTHH:mm" or "...:ss".
    return parseDateTime(value.length === 16 ? `${value}:00` : value);
  } catch {
    return null;
  }
}

function formatLocal(v: CalendarDateTime | null): string {
  if (!v) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${v.year}-${pad(v.month)}-${pad(v.day)}T${pad(v.hour)}:${pad(v.minute)}`;
}

export function DateTimeField({
  value,
  onChange,
  id,
  ariaLabel,
  required,
  disabled,
  invalid = false,
  className,
  style,
}: DateTimeFieldProps) {
  const dateValue = safeParse(value);
  const classes = ["datefield", invalid ? "datefield--invalid" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");
  return (
    <DatePicker
      value={dateValue}
      onChange={(v) =>
        onChange(v && "hour" in v ? formatLocal(v as CalendarDateTime) : "")
      }
      aria-label={ariaLabel}
      isRequired={required}
      isDisabled={disabled}
      granularity="minute"
      hourCycle={24}
      shouldCloseOnSelect={false}
    >
      <Group id={id} className={classes} style={style}>
        <DateInput className="datefield__input">
          {(segment) => <DateSegment segment={segment} className="datefield__seg" />}
        </DateInput>
        <RACButton className="datefield__trigger" aria-label="open calendar">
          <CalendarIcon />
        </RACButton>
      </Group>
      <Popover className="datefield__popover" placement="bottom start" offset={6}>
        <Dialog className="datefield__dialog">
          <Calendar className="datefield__calendar">
            <header className="datefield__cal-header">
              <RACButton slot="previous" className="datefield__nav">‹</RACButton>
              <Heading className="datefield__heading" />
              <RACButton slot="next" className="datefield__nav">›</RACButton>
            </header>
            <CalendarGrid className="datefield__grid">
              <CalendarGridHeader>
                {(day) => (
                  <CalendarHeaderCell className="datefield__weekday">
                    {day}
                  </CalendarHeaderCell>
                )}
              </CalendarGridHeader>
              <CalendarGridBody>
                {(date) => (
                  <CalendarCell date={date} className="datefield__cell" />
                )}
              </CalendarGridBody>
            </CalendarGrid>
          </Calendar>
        </Dialog>
      </Popover>
    </DatePicker>
  );
}

function CalendarIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
